/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Button, Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import { CheckCircle, Copy, File, UploadSimple, X } from '@phosphor-icons/react';
import { useState } from 'react';

import { fetchAPI } from '@/api/fetch';
import { apiAdminExtractJD } from '@/api/services';
import { useAdminAnalysis } from '@/store/adminAnalysis';
import { JobDescription } from '@/types/JobDescription';
import { getEmploymentTypeLabel, getWorkTypeLabel } from '@/types/enums';
import RichEditor from '@/components/forms/RichEditor';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

export default function JDExtractorSection() {
  const fileSizeLimit = MAX_RESUME_FILE_SIZE;
  const [fileUploading, setFileUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const {
    jdFile,
    setJDFile,
    clearJDFile,
    jobDescription,
    setJobDescription,
    clearJobDescription,
    clearResumeAnalysisResult
  } = useAdminAnalysis();

  const getFileSize = (size?: number): string => {
    if (!size) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return `${(size / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setJDFile(file);
    setFileUploading(true);
    clearJobDescription();
    clearResumeAnalysisResult();

    if (file.size > fileSizeLimit) {
      setFileError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      setFileUploading(false);
      return;
    }

    try {
      const response = await fetchAPI<JobDescription>({
        request: () => apiAdminExtractJD({ payload: { file } })
      });

      if (response.success) {
        const formattedDescription = response.data.job_description?.replace(/\\n/g, '\n') || '';
        setJobDescription({
          ...response.data,
          job_description: formattedDescription
        });
        clearResumeAnalysisResult();
      } else {
        handleFileError('Unable to extract job description', response.message);
      }
    } catch (error) {
      handleFileError('Unable to extract job description', error);
    } finally {
      setFileUploading(false);
    }
  };

  const handleFileError = (message: string, error: any) => {
    setFileError(message);
    console.error(message, error);
  };

  const formatCurrency = (value: number): string => {
    return value ? `$${value.toLocaleString()}` : '';
  };

  return (
    <Stack gap="24px" width="100%">
      <Stack gap="16px" align="flex-start" width="100%">
        {jdFile || fileUploading ? (
          <Box display="flex" alignItems="center" width="100%">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              width="150px"
              height="150px"
              borderRadius="12px"
              border="1px solid"
              borderColor={fileError ? 'error.500' : 'primary.100'}
              bg="primary.50"
            >
              {fileUploading ? (
                <>
                  <Spinner size="lg" borderWidth="4px" color="primary.900" />
                  <Box mt="8px" fontSize="16px" color="neutrals.900" textAlign="center">
                    Uploading
                  </Box>
                </>
              ) : (
                <File size={64} color="#4e76ff" />
              )}
            </Box>
            <Box ml="32px" color="neutrals.900">
              <Box display="flex" alignItems="center" mb="4px">
                {!fileUploading && (
                  <Box mr="12px">
                    <CheckCircle size={24} color="#4e76ff" weight="fill" />
                  </Box>
                )}
                <Box fontSize="18px" color="neutrals.800" mr="16px">
                  {jdFile?.name}
                </Box>
                {!fileUploading && (
                  <X
                    size={16}
                    color="#545454"
                    onClick={() => {
                      clearJDFile();
                      clearJobDescription();
                      setFileError(null);
                      clearResumeAnalysisResult();
                    }}
                    cursor="pointer"
                  />
                )}
              </Box>
              <Box fontSize="16px" color={fileError ? 'error.700' : 'neutrals.500'}>
                {getFileSize(jdFile?.size)}
              </Box>
              {fileError && (
                <Box fontSize="16px" color="error.700" mt="4px">
                  {fileError}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            w="100%"
            h="200px"
            border="1px dashed"
            borderColor="neutrals.300"
            borderRadius="12px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            _hover={{ borderColor: 'neutrals.300' }}
            bg="neutrals.50"
          >
            <Box
              position="relative"
              width="100%"
              height="200px"
              border="2px dashed"
              borderColor="neutrals.300"
              borderRadius="12px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              _hover={{ borderColor: 'neutrals.300' }}
              bg="neutrals.50"
            >
              <input
                id="fileInput"
                type="file"
                accept=".pdf,.txt"
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
                onChange={onUploadFile}
              />
              <Box display="flex" flexDirection="column" alignItems="center">
                <UploadSimple size={60} color="#404040" />
                <Box textAlign="center" color="neutrals.900">
                  Drop file here or{' '}
                  <Box as="span" color="primary.900">
                    click to upload
                  </Box>
                </Box>
                <Box color="neutrals.500" fontSize="14px">
                  Upload PDF, or TXT file types ({MAX_RESUME_FILE_SIZE_MB} MB max)
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Stack>
      <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
        Extracted Job Description
      </Text>
      {jobDescription ? (
        <Box w="100%" border="1px solid" borderColor="neutrals.300" borderRadius="12px">
          <Stack p="16px" gap="16px">
            {[
              { label: 'Job Title', value: jobDescription.job_title },
              {
                label: 'Salary Currency',
                value: jobDescription.salary_currency || 'N/A'
              },
              {
                label: 'Salary Range',
                value: `${formatCurrency(jobDescription.salary_from)} to ${formatCurrency(
                  jobDescription.salary_to
                )}`
              },
              {
                label: 'Employment Type',
                value: getEmploymentTypeLabel(jobDescription.employment_type)
              },
              {
                label: 'Work Type',
                value: getWorkTypeLabel(jobDescription.work_type)
              },
              { label: 'Location', value: jobDescription.location }
            ].map(({ label, value }) => (
              <Flex key={label} gap="8px">
                <Text fontSize="16px" color="neutrals.800" fontWeight="semibold">
                  {label}:
                </Text>
                <Text fontSize="16px" color="neutrals.800">
                  {value}
                </Text>
              </Flex>
            ))}
            <Stack gap="8px">
              <Text fontSize="16px" color="neutrals.800" fontWeight="semibold">
                Job Description:
              </Text>
              <Box
                position="relative"
                fontSize="14px"
                color="neutrals.700"
                border="1px solid"
                borderColor="neutrals.300"
                p="8px"
                borderRadius="8px"
              >
                <Button
                  zIndex={1500}
                  position="absolute"
                  top="8px"
                  right="8px"
                  size="xs"
                  onClick={() => {
                    navigator.clipboard.writeText(jobDescription.job_description);
                    alert('Job description copied to clipboard!');
                  }}
                >
                  <Copy size={24} />
                </Button>
                <RichEditor
                  key="job-description"
                  content={jobDescription.job_description}
                  type="job-description"
                  readOnly={true}
                  className="read-only"
                />
              </Box>
            </Stack>
            <Text fontSize="20px" color="neutrals.800" fontWeight="semibold">
              Contact
            </Text>
            {[
              { label: 'Name', value: jobDescription.contact_name },
              { label: 'Role', value: jobDescription.contact_role },
              { label: 'Email', value: jobDescription.contact_email },
              { label: 'Phone', value: jobDescription.contact_phone }
            ].map(({ label, value }) => (
              <Flex key={label} gap="8px">
                <Text fontSize="16px" color="neutrals.800" fontWeight="semibold">
                  {label}:
                </Text>
                <Text fontSize="14px" color="neutrals.700">
                  {value || 'N/A'}
                </Text>
              </Flex>
            ))}
          </Stack>
        </Box>
      ) : (
        <Text>No job description extracted yet.</Text>
      )}
    </Stack>
  );
}
